import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Send, Trash2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function authorInitials(name) {
  const parts = (name || "").trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "RQ";
}

/**
 * Threaded comments — reusable across blog articles and community insights.
 * @param {string} targetType "blog" | "insight"
 * @param {string} targetSlug slug (or ObjectId) of the target resource
 */
export default function ThreadedComments({ targetType, targetSlug, autoLoad = true, headingClass = "" }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [newText, setNewText] = useState("");
  const [posting, setPosting] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = useCallback(async () => {
    if (!targetSlug) return;
    try {
      const r = await axios.get(`${API}/api/comments/${targetType}/${encodeURIComponent(targetSlug)}`);
      setComments(r.data || []);
    } catch { /* silent */ }
    finally { setLoaded(true); }
  }, [targetType, targetSlug]);

  useEffect(() => { if (autoLoad) load(); }, [load, autoLoad]);

  const submit = async (parentId = null, text = null) => {
    const content = (text ?? newText).trim();
    if (!content) return;
    if (!user) { window.location.href = "/login"; return; }
    setPosting(true);
    try {
      const r = await axios.post(`${API}/api/comments/${targetType}/${encodeURIComponent(targetSlug)}`, { content, parent_id: parentId }, { withCredentials: true });
      setComments(prev => [...prev, r.data]);
      if (parentId) { setReplyText(""); setReplyingTo(null); }
      else { setNewText(""); }
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't post comment");
    } finally { setPosting(false); }
  };

  const likeComment = async (commentId) => {
    if (!user) { window.location.href = "/login"; return; }
    try {
      const r = await axios.post(`${API}/api/comments/${commentId}/like`, {}, { withCredentials: true });
      setComments(prev => prev.map(c => c._id === commentId ? { ...c, likes: r.data.likes } : c));
    } catch { /* silent */ }
  };

  const deleteComment = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/api/comments/${commentId}`, { withCredentials: true });
      setComments(prev => prev.filter(c => c._id !== commentId && c.parent_id !== commentId));
    } catch (err) { alert(err?.response?.data?.detail || "Delete failed"); }
  };

  // Build tree
  const childrenMap = {};
  const roots = [];
  comments.forEach(c => {
    const p = c.parent_id;
    if (p) { (childrenMap[p] = childrenMap[p] || []).push(c); }
    else roots.push(c);
  });

  const renderComment = (c, depth = 0) => {
    const children = childrenMap[c._id] || [];
    const indent = Math.min(depth, 2) * 24;
    const canDelete = user && (user._id === c.author_id || user.role === "admin");
    const isReplying = replyingTo === c._id;
    return (
      <div key={c._id} style={{ marginLeft: indent }} className="mb-3" data-testid={`comment-${c._id}`}>
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-dm text-[11px] font-semibold" style={{ background: "#0F1E35", color: "#C9A84C" }}>
            {authorInitials(c.author_name)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="bg-cream/60 rounded-2xl rounded-tl-md px-4 py-2.5">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="font-dm text-navy text-xs font-semibold">{c.author_name || "Community Member"}</span>
                {c.author_role && <span className="font-dm text-navy/40 text-[10px]">{c.author_role}</span>}
                <span className="font-dm text-navy/30 text-[10px]">· {timeAgo(c.created_at)}</span>
              </div>
              <p className="font-dm text-navy/80 text-sm leading-relaxed break-words whitespace-pre-wrap">{c.content}</p>
            </div>
            <div className="flex items-center gap-4 mt-1.5 pl-3">
              <button onClick={() => likeComment(c._id)} className="flex items-center gap-1 text-navy/40 hover:text-gold font-dm text-[11px] transition-colors" data-testid={`like-comment-${c._id}`}>
                <Heart className="w-3 h-3" /> {c.likes || 0}
              </button>
              {user && depth < 2 && (
                <button onClick={() => { setReplyingTo(isReplying ? null : c._id); setReplyText(""); }} className="text-navy/40 hover:text-gold font-dm text-[11px] font-semibold transition-colors" data-testid={`reply-btn-${c._id}`}>
                  {isReplying ? "Cancel" : "Reply"}
                </button>
              )}
              {canDelete && (
                <button onClick={() => deleteComment(c._id)} className="text-navy/30 hover:text-red-500 font-dm text-[11px] transition-colors" data-testid={`delete-comment-${c._id}`}>
                  <Trash2 className="w-3 h-3 inline" />
                </button>
              )}
            </div>
            {isReplying && (
              <div className="mt-2 flex gap-2" data-testid={`reply-form-${c._id}`}>
                <input value={replyText} onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Reply to ${(c.author_name || "").split(" ")[0] || "comment"}…`}
                  onKeyDown={(e) => { if (e.key === "Enter" && replyText.trim()) submit(c._id, replyText); }}
                  className="flex-1 border border-navy/15 rounded-lg px-3 py-1.5 font-dm text-xs focus:border-gold focus:outline-none" />
                <button onClick={() => submit(c._id, replyText)} disabled={posting || !replyText.trim()} data-testid={`submit-reply-${c._id}`}
                  className="bg-navy text-gold px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50">Reply</button>
              </div>
            )}
          </div>
        </div>
        {children.length > 0 && (
          <div className="border-l-2 border-navy/5 pl-4 mt-2">
            {children.map(child => renderComment(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="mt-12 pt-8 border-t border-navy/10" data-testid={`comments-section-${targetType}`}>
      <h3 className={headingClass || "font-playfair text-navy text-2xl font-semibold mb-5 flex items-center gap-2"}>
        <MessageCircle className="w-5 h-5 text-gold" />
        Comments {loaded && <span className="text-navy/40 text-base font-normal">({comments.length})</span>}
      </h3>

      {/* Compose */}
      {user ? (
        <div className="flex gap-3 mb-6" data-testid="comments-compose">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-dm text-xs font-semibold" style={{ background: "#0F1E35", color: "#C9A84C" }}>
            {authorInitials(user.name)}
          </div>
          <div className="flex-1">
            <textarea value={newText} onChange={(e) => setNewText(e.target.value)} rows={2} maxLength={2000}
              placeholder="Add your perspective…"
              data-testid="comment-input"
              className="w-full border border-navy/15 rounded-2xl px-4 py-2.5 font-dm text-sm focus:border-gold focus:outline-none resize-none" />
            <div className="flex items-center justify-between mt-1">
              <span className="font-dm text-navy/30 text-[10px]">Be respectful — comments are moderated.</span>
              <button onClick={() => submit(null)} disabled={posting || !newText.trim()} data-testid="submit-comment"
                className="bg-navy text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg hover:bg-navy/90 disabled:opacity-50 flex items-center gap-1.5">
                <Send className="w-3 h-3" /> {posting ? "Posting…" : "Post comment"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-cream rounded-2xl p-5 mb-6 text-center" data-testid="comments-login-prompt">
          <p className="font-dm text-navy/60 text-sm">
            <Link to="/login" className="text-gold font-semibold hover:underline">Log in</Link> or <Link to="/signup" className="text-gold font-semibold hover:underline">sign up</Link> to join the discussion.
          </p>
        </div>
      )}

      {/* Thread */}
      {!loaded ? (
        <p className="font-dm text-navy/40 text-sm py-6 text-center">Loading comments…</p>
      ) : roots.length === 0 ? (
        <p className="font-dm text-navy/40 text-sm py-6 text-center" data-testid="comments-empty">No comments yet. Be the first to share your perspective.</p>
      ) : (
        <div data-testid="comments-thread">
          {roots.map(r => renderComment(r, 0))}
        </div>
      )}
    </section>
  );
}
